import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Space, Skeleton } from "antd";
import {
    Channels,
    FilterPosition,
    FrequencySlicerOptions,
    OptionValues,
    RevstackCubeIds,
    RevstackFilterTypes,
    RevstackFrequencySlicerOptions,
    WidgetConfig,
    WidgetData,
    WidgetType
} from "@ts/common-types";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { sortChartDataByFiltersDataOrder } from "@/ts/revstack/helpers";
import { getRevstackWidgetData, getFiltersFromCube } from "@/ts/retrieve-data";
import { DATE_PICKER_PRESET_PERIODS, granularityOptions, REVSTACK_CHART_COLORS, REVSTACK_DRILL_DOWN_PERIOD_OPTIONS, REVSTACK_TRENDS_LIGHT_CHART_COLORS } from "@/ts/constants";
import RevstackTrendsSelectDropDown from "../drop-downs/RevstackTrendsSelectDropDown";
import { capitalizeWords, startCase, titleCase } from "@/ts/utils";
import { useSelector } from "react-redux";
import { flattenWidgetStructure } from "@/ts/revstack/renderers";

interface IRevstackGenericChartParentContainer {
    widgetData: WidgetData;
    widgetConfig: {
        tabKey: any;
        icon: React.ReactNode;
        widgets: WidgetConfig[][];
        formatter: (widgetConfig: WidgetConfig, widget: WidgetConfig, widgetData: WidgetData) => any;
        config: {
            sortByFiltersData: boolean;
            filterToSkip: string;
            queryMetaInfo: any;
            pillsDropDownConfig: {
                formatForChannelId: boolean;
                cubeId: RevstackCubeIds;
                queryId: string;
                queryDimensions: string[];
                isExcludeDimension: boolean;
                excludeFilter?: string[];
                queryMetaInfo: any;
                filterToSkip: string;
            };
            formatForChannelId: boolean;
            chartPillFiltersDatakeys: { title: string; subTitle: string; metricToDisplay: string; metricUnit: string; value: string; metricValue: string };
            layout: Record<string, any>;
            isTrendsButtonRequired?: boolean;
            isDeepDiveButtonRequired?: boolean;
            isDrillDownButtonRequired?: boolean;
            isBodyPaddingReq?: boolean;
            componentFilterType?: string;
            relatedComponentDataKeys?: string[];
            // Pills configuration
            hasPillsFilters?: boolean;
            hasFilterDropDown?: boolean;
            isRemovablePillsRequired?: boolean;
            addPillsBtnTitle?: string;
            maxPillsFiltersAllowed?: number;
            capitalizePillNames?: boolean;
            pillsData?: any[];
            chartPillFiltersOptions?: any[];
            graphColors?: string[];
            primaryDimension?: string;
            disableYaxisOppositeOnPillCount?: number | null;
            excludeYAxisFromOpposite?: boolean;
            // Tooltip synchronization
            enableTooltipSync?: boolean;
            syncGroupId?: string;
            dropdownFilterPlaceHolder?: string;
        };
    };
    title: string;
    selectedTimePeriod?: DATE_PICKER_PRESET_PERIODS;
    granularity?: RevstackFrequencySlicerOptions;
    handlePeriodAndGranularityChange?: (timePeriod: DATE_PICKER_PRESET_PERIODS, granularity: RevstackFrequencySlicerOptions) => void;
    activeTab?: string;
    pillFilterDropDownData?: Record<string, any[]>;
    setPillFilterDropDownData?: (cache: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)) => void;
}

const CardHeaderSection: React.FC<{
    title: string;
    titleIcon: React.ReactNode;
}> = React.memo(({ title, titleIcon }) => (
    <div className="d-flex gap-1 align-items-center w-100">
        <Space align="center" direction="horizontal" size={12}>
            <div className="icon-btn">{titleIcon}</div>
            <Space align="center" direction="horizontal" size={12}>
                <h4 className="mb-0">{title || "Performance Table"}</h4>
            </Space>
        </Space>
    </div>
));

const RevstackGenericChartParentContainer: React.FC<
    IRevstackGenericChartParentContainer
> = ({ widgetConfig,
    widgetData,
    title,
    selectedTimePeriod,
    granularity,
    handlePeriodAndGranularityChange,
    activeTab,
    pillFilterDropDownData,
    setPillFilterDropDownData }) => {

        const chartRefs = useRef<Map<string, React.RefObject<any>>>(new Map());

        const getChartRef = (chartId: string): React.RefObject<any> => {
            if (!chartRefs.current.has(chartId)) {
                chartRefs.current.set(chartId, React.createRef());
            }
            return chartRefs.current.get(chartId)!;
        };

        const getChartSyncId = (widget: any): string => {
            if (widget.config?.chartId) {
                return widget.config.chartId;
            }

            return widget.identifier || 'widget-fallback';
        };

        const trendsFilters = useSelector(
            (state: { trendsFilters: { value: any } }) =>
                state.trendsFilters.value
        );

        const [pillsData, setPillsData] = useState<any[]>([]);
        // Use activeTab-based state to prevent cross-tab interference
        const [selectedPillsByTab, setSelectedPillsByTab] = useState<Record<string, any[]>>({});
        const pillsInitializedRef = useRef<Record<string, boolean>>({});

        const currentTabKey = activeTab || widgetConfig?.tabKey || 'default';
        const selectedPills = selectedPillsByTab[currentTabKey] || [];

        const setSelectedPills = (pills: any[] | ((prev: any[]) => any[])) => {
            setSelectedPillsByTab(prev => ({
                ...prev,
                [currentTabKey]: typeof pills === 'function' ? pills(prev[currentTabKey] || []) : pills
            }));
        };

        // Initialize parentFilterValues with defaultFilter values
        const initialParentFilterValues = useMemo(() => {
            const initialValues: Record<string, string | string[]> = {};

            if (widgetConfig?.widgets) {
                // Extract all actual widget objects from the nested structure
                const allWidgets = flattenWidgetStructure(widgetConfig.widgets);

                allWidgets.forEach((widget: any) => {
                    // Skip widgets that are configured to not contribute parent filters
                    if (widget.config?.skipParentFilters === true) {
                        return;
                    }

                    const formatterOutput = widget.formatter(widgetConfig, widget, widgetData);

                    if (formatterOutput.parentFilters && formatterOutput.parentFilters.length > 0) {
                        formatterOutput.parentFilters.forEach((filter: any) => {
                            if (filter.defaultFilter !== undefined) {
                                initialValues[filter.dataKey] = filter.defaultFilter;
                            }
                        });
                    }
                });
            }

            return initialValues;
        }, [widgetConfig?.widgets, widgetData]);

        const [parentFilterValues, setParentFilterValues] = useState<
            Record<string, string | string[]>
        >(initialParentFilterValues);
        const previousValueOnOpen = useRef<string | string[]>(null);

        // Update parentFilterValues when initialParentFilterValues change
        useEffect(() => {
            setParentFilterValues(initialParentFilterValues);
        }, [initialParentFilterValues]);

        const hasChartPillsFilters = widgetConfig?.config?.layout?.hasChartPillsFilters;
        const primaryDimension = widgetConfig?.config?.queryMetaInfo?.primaryDimension || "subcategory";
        const resultKey = widgetConfig?.config?.queryMetaInfo?.resultKey || "subcategory_trends_modal_data";
        const hasFilterDropDown = widgetConfig?.config?.layout?.hasFilterDropDown || false;
        const isRemovablePillsRequired = widgetConfig?.config?.isRemovablePillsRequired || false;
        const addPillsBtnTitle = widgetConfig?.config?.addPillsBtnTitle || "Add Filter";
        const maxPillsFiltersAllowed = widgetConfig?.config?.maxPillsFiltersAllowed || 5;
        const capitalizePillNames = widgetConfig?.config?.capitalizePillNames || false;
        const graphColors = widgetConfig?.config?.graphColors || REVSTACK_TRENDS_LIGHT_CHART_COLORS;
        const disableYaxisOppositeOnPillCount = widgetConfig?.config?.disableYaxisOppositeOnPillCount || null;
        const excludeYAxisFromOpposite = widgetConfig?.config?.excludeYAxisFromOpposite || false;
        const sortByFiltersData = widgetConfig?.config.sortByFiltersData || false;
        const enableTooltipSync = widgetConfig?.config?.enableTooltipSync || false;
        const dropdownFilterPlaceHolder = widgetConfig?.config?.dropdownFilterPlaceHolder || "Search Sub Categories";
        // Check for data with or without _percent/_non_percent suffixes
        const shouldShowLoading = !widgetData[resultKey] &&
            !widgetData[`${resultKey}_percent`] &&
            !widgetData[`${resultKey}_non_percent`]

        const filterParams = useMemo(() => ({
            cubeId: widgetConfig?.config?.pillsDropDownConfig?.cubeId,
            queryId: widgetConfig?.config?.pillsDropDownConfig?.queryId || "",
            queryDimensions: widgetConfig?.config?.pillsDropDownConfig?.queryDimensions || [],
            isExcludeDimension: widgetConfig?.config?.pillsDropDownConfig?.isExcludeDimension || false,
            excludeFilter: widgetConfig?.config?.pillsDropDownConfig?.excludeFilter || [],
            filterToSkip: widgetConfig?.config?.pillsDropDownConfig?.filterToSkip || "",
            dimValueToTruncate: widgetConfig?.config?.queryMetaInfo?.dimValueToTruncate
        }), [
            widgetConfig?.config?.pillsDropDownConfig?.cubeId,
            widgetConfig?.config?.pillsDropDownConfig?.queryId,
            JSON.stringify(widgetConfig?.config?.pillsDropDownConfig?.queryDimensions),
            widgetConfig?.config?.pillsDropDownConfig?.isExcludeDimension,
            JSON.stringify(widgetConfig?.config?.pillsDropDownConfig?.excludeFilter),
            widgetConfig?.config?.pillsDropDownConfig?.filterToSkip,
            trendsFilters,
            widgetConfig?.config?.queryMetaInfo?.dimValueToTruncate
        ]);

        useEffect(() => {
            const { cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip, dimValueToTruncate } = filterParams;

            if (cubeId && queryId && queryDimensions?.length > 0) {

                // Fetch new filter data and store it for this tab
                getFiltersFromCube(cubeId, queryId, queryDimensions, isExcludeDimension, excludeFilter, filterToSkip, false, dimValueToTruncate).then((data) => {
                    setPillFilterDropDownData(prev => ({
                        ...prev,
                        [currentTabKey]: data
                    }));
                });

            }
        }, [filterParams, sortByFiltersData, hasChartPillsFilters, currentTabKey, trendsFilters]);

        // Initialize pills data from widget data
        useEffect(() => {
            if (!hasChartPillsFilters) return;

            // If sorting is required but dropdown data is not ready, don't proceed
            if (sortByFiltersData && (!pillFilterDropDownData[currentTabKey] || pillFilterDropDownData[currentTabKey].length === 0)) {
                return;
            }

            // Get the original series data from the first widget that has chartDataSeries
            let originalSeries: any[] = [];
            if (widgetConfig?.widgets) {
                // Extract all actual widget objects from the nested structure
                const allWidgets = flattenWidgetStructure(widgetConfig.widgets);

                for (const widget of allWidgets) {
                    if (widget.config?.extractPillsData) {
                        continue;
                    }
                    const componentData = widget.formatter?.(widgetConfig, widget, widgetData)?.componentData;
                    if (componentData?.chartDataSeries?.series) {
                        originalSeries = JSON.parse(JSON.stringify(componentData.chartDataSeries.series));
                        break;
                    }
                }

            }

            if (originalSeries.length === 0) return;

            let uniqueItems = [...new Set(
                originalSeries.map((item: any) => item[primaryDimension])
            )];

            uniqueItems = sortChartDataByFiltersDataOrder(uniqueItems, pillFilterDropDownData[currentTabKey], primaryDimension, sortByFiltersData);

            // Create color map based on the SORTED unique items order
            const colorMap = new Map();
            uniqueItems.forEach((item, index) => {
                const color = graphColors[index];
                colorMap.set(item, color);
            });

            const pillData = uniqueItems.map((item) => {
                return {
                    name: titleCase(item as string),
                    value: item,
                    color: colorMap.get(item) || graphColors[0],
                };
            });

            setPillsData(pillData);

            // Only initialize selected pills if this tab hasn't been initialized yet
            // This prevents overwriting user's manual pill selections
            if (!pillsInitializedRef.current[currentTabKey]) {
                const initialSelectedPills = pillData.slice(0, maxPillsFiltersAllowed);
                setSelectedPills(initialSelectedPills);
                pillsInitializedRef.current = {
                    ...pillsInitializedRef.current,
                    [currentTabKey]: true
                };
            } else {
                const currentSelectedValues = selectedPills.map((pill: any) => pill.value);
                let updatedSelectedPills = pillData.filter((pill: any) =>
                    currentSelectedValues.includes(pill.value)
                );

                // Reassign colors based on position within selected pills (not full list)
                updatedSelectedPills = updatedSelectedPills.map((pill, index) => {
                    let color;
                    if (updatedSelectedPills.length === 1) {
                        color = REVSTACK_CHART_COLORS.primary[700];
                    } else {
                        // Multiple pills get colors based on their position in selected pills
                        color = graphColors[index] || REVSTACK_TRENDS_LIGHT_CHART_COLORS[index % REVSTACK_TRENDS_LIGHT_CHART_COLORS.length];
                    }
                    return { ...pill, color };
                });

                const hasOrderChanged = updatedSelectedPills.some((pill: any, index: number) =>
                    selectedPills[index]?.value !== pill.value
                );

                const hasColorChanged = updatedSelectedPills.some((pill: any, index: number) =>
                    selectedPills[index]?.color !== pill.color
                );

                if (hasOrderChanged || hasColorChanged) {
                    setSelectedPills(updatedSelectedPills);
                }
            }

        }, [hasChartPillsFilters, widgetData, primaryDimension, graphColors, maxPillsFiltersAllowed,
            sortByFiltersData, widgetConfig?.widgets, pillFilterDropDownData[currentTabKey], currentTabKey]);

        // Re-initialize pills data on change of specific filters
        useEffect(() => {

            pillsInitializedRef.current[currentTabKey] = false;

        }, [trendsFilters?.selected?.channel,
        trendsFilters?.selected?.secondary_region,
            currentTabKey,
            pillFilterDropDownData]);


        const chartPillFiltersOptions = useMemo(() => {
            const dataKeys = widgetConfig?.config?.chartPillFiltersDatakeys;
            const formatChannelId = widgetConfig?.config?.pillsDropDownConfig?.formatForChannelId || false;

            return pillFilterDropDownData[currentTabKey]?.map((item: any) => ({
                title: formatChannelId ? startCase(Channels[item[dataKeys?.title]]) : item[dataKeys?.title] || "",
                subtitle: item[dataKeys?.subTitle] || "",
                metric: dataKeys?.metricToDisplay || "",
                metricValue: `${item[dataKeys?.metricValue] || 0} ${dataKeys?.metricUnit || ""}`,
                value: formatChannelId ? startCase(Channels[item[dataKeys?.title]]) : item[dataKeys?.title] || "",
            })) || [];
        }, [pillFilterDropDownData, currentTabKey]);

        const handleRemovePill = (value: string) => {
            if (selectedPills.length > 1) {
                let newSelectedPills = selectedPills.filter((pill: any) => pill.value !== value);

                // Reassign colors based on new positions
                newSelectedPills = newSelectedPills.map((pill, index) => {
                    let color;
                    if (newSelectedPills.length === 1) {
                        color = REVSTACK_CHART_COLORS.primary[700];
                    } else {
                        color = graphColors[index];
                    }
                    return { ...pill, color };
                });

                setSelectedPills(newSelectedPills);
            }
        };

        const handleAddPill = (value: string) => {
            const pillToAdd = pillsData.find((pill: any) => pill.value === value);

            if (pillToAdd && !selectedPills.some((pill: any) => pill.value === value)) {
                let newSelectedPills = [...selectedPills, pillToAdd];

                // Reassign colors based on position within selected pills
                newSelectedPills = newSelectedPills.map((pill, index) => {
                    let color;
                    if (newSelectedPills.length === 1) {
                        color = REVSTACK_CHART_COLORS.primary[700];
                    } else {
                        color = graphColors[index];
                    }
                    return { ...pill, color };
                });

                setSelectedPills(newSelectedPills);
            }
        };

        const parentFilters = useMemo(() => {
            const filters: any[] = [];

            if (widgetConfig?.widgets) {
                // Extract all actual widget objects from the nested structure
                const allWidgets = flattenWidgetStructure(widgetConfig.widgets);

                allWidgets.forEach((widget: any) => {
                    // Skip widgets that are configured to not contribute parent filters
                    if (widget.config?.skipParentFilters === true) {
                        return;
                    }

                    const formatterOutput = widget.formatter(widgetConfig, widget, widgetData);

                    if (formatterOutput.parentFilters && formatterOutput.parentFilters.length > 0) {
                        formatterOutput.parentFilters.forEach((filter: any) => {
                            const currentValue = parentFilterValues[filter.dataKey] || filter.defaultFilter;
                            let validCurrentValue;
                            const isMultiSelect = filter.type === RevstackFilterTypes.MULTI_SELECT;
                            if (isMultiSelect) {
                                const defaultValue = Array.isArray(filter.defaultFilter)
                                    ? filter.defaultFilter
                                    : filter.defaultFilter
                                        ? [filter.defaultFilter]
                                        : [filter.filterOptions?.[0]?.value].filter(Boolean);

                                validCurrentValue = Array.isArray(currentValue)
                                    ? currentValue
                                    : currentValue
                                        ? [currentValue]
                                        : defaultValue;

                                validCurrentValue = validCurrentValue.filter((value: string) =>
                                    filter.filterOptions?.find((opt: any) => opt.value === value)
                                );

                                if (validCurrentValue.length === 0) {
                                    validCurrentValue = defaultValue;
                                }
                            } else {
                                const defaultValue = filter.defaultFilter || filter.filterOptions?.[0]?.value;
                                validCurrentValue = filter.filterOptions?.find((opt: any) => opt.value === currentValue)
                                    ? currentValue
                                    : defaultValue;
                            }

                            const filterType = filter.type;

                            // Check if this is the yAxisOppositeFilter and should be disabled based on pills count
                            const shouldDisableFilter = shouldShowLoading || (
                                filter.dataKey === "yAxisOppositeFilter" &&
                                disableYaxisOppositeOnPillCount !== null &&
                                selectedPills.length !== disableYaxisOppositeOnPillCount);

                            let filteredOptions = filter.filterOptions || [];

                            // Exclude the selected yAxis metric from yAxisOpposite options
                            if (excludeYAxisFromOpposite && filter.filterOptions) {
                                // Only apply disabling logic when the condition is met (pill count matches the disable threshold)
                                if (disableYaxisOppositeOnPillCount !== null && selectedPills.length === disableYaxisOppositeOnPillCount) {
                                    if (filter.dataKey === "yAxisFilter") {
                                        const oppositeValue = parentFilterValues?.yAxisOppositeFilter;
                                        if (oppositeValue) {
                                            filteredOptions = filter.filterOptions.map((opt: any) => ({
                                                ...opt,
                                                disabled: opt.value === oppositeValue
                                            }));
                                        }
                                    } else if (filter.dataKey === "yAxisOppositeFilter") {
                                        const yAxisValue = parentFilterValues?.yAxisFilter;
                                        if (yAxisValue) {
                                            filteredOptions = filter.filterOptions.map((opt: any) => ({
                                                ...opt,
                                                disabled: opt.value === yAxisValue
                                            }));
                                        }
                                    }
                                }
                                // When condition is not met, filteredOptions remains as filter.filterOptions (no disabling applied)
                            }

                            const commonProps = {
                                options: filteredOptions,
                                width: filter.width || 180,
                                placeholder: filter.placeholder || `Select ${filter.dataKey}`,
                            };

                            const actions = filter?.actions || {};

                            let onChange = (value: string[]) => {
                                setParentFilterValues(prev => ({
                                    ...prev,
                                    [filter.dataKey]: value
                                }));
                            };

                            let onOpenChange = (open: boolean) => { };

                            //TODO: Apply these edge cases onChange also if required same logic...
                            if (actions?.onOpenChange === "get-data") {
                                onOpenChange = async (open: boolean) => {
                                    if (open) {
                                        previousValueOnOpen.current = validCurrentValue;
                                    } else {
                                        // Edge case 1: When options have ALL and +1 filter option available, don't trigger API call
                                        const hasAllOption = filter.filterOptions?.some((opt: any) =>
                                            opt.value === OptionValues.ALL
                                        );

                                        const totalOptions = filter.filterOptions?.length || 0;
                                        const hasAllAndOneMore = hasAllOption && totalOptions === 2;

                                        // Edge case 2: If current selection is not changed during dropdown interaction, don't trigger API call
                                        const isSelectionUnchanged = JSON.stringify(validCurrentValue) === JSON.stringify(previousValueOnOpen.current);

                                        if (hasAllAndOneMore || isSelectionUnchanged) {
                                            return;
                                        }

                                        await getRevstackWidgetData(
                                            actions?.route,
                                            widgetConfig,
                                            widgetConfig?.config?.componentFilterType,
                                            validCurrentValue,
                                            widgetConfig?.config?.relatedComponentDataKeys || []
                                        );
                                    }
                                }
                            }

                            const filterConfig = {
                                section: "" as const,
                                type: filterType,
                                actions,
                                props: {
                                    ...commonProps,
                                    value: validCurrentValue,
                                    ...(filterType === RevstackFilterTypes.MULTI_SELECT && {
                                        onChange,
                                        onOpenChange,
                                        showAllOption: (() => {
                                            if (filter.showAllOption === false) return false;

                                            const allOptionValue = filter.allOptionValue || OptionValues.ALL;
                                            const hasExistingAllOption = filter.filterOptions?.some((opt: any) =>
                                                opt.value === allOptionValue
                                            );

                                            return !hasExistingAllOption;
                                        })(),
                                        allOptionValue: filter.allOptionValue || OptionValues.ALL,
                                        allOptionLabel: (() => {
                                            if (filter.allOptionLabel) return filter.allOptionLabel;

                                            const allOptionValue = filter.allOptionValue || OptionValues.ALL;
                                            const existingAllOption = filter.filterOptions?.find((opt: any) =>
                                                opt.value === allOptionValue
                                            );

                                            return existingAllOption?.label || `All ${filter.dataKey}`;
                                        })(),
                                        maxDisplayItems: filter.maxDisplayItems || 2,
                                        showOnlyAllLabelWhenAllSelected: filter.showOnlyAllLabelWhenAllSelected || false,
                                    }),
                                    ...(filterType !== RevstackFilterTypes.MULTI_SELECT && {
                                        onChange: (value: string) => {
                                            setParentFilterValues(prev => ({
                                                ...prev,
                                                [filter.dataKey]: value
                                            }));
                                        },
                                    }),
                                },
                                disabled: shouldDisableFilter,
                            };

                            filters.push(filterConfig);
                        });
                    }
                });
            }

            return filters;
        }, [widgetConfig, widgetData, parentFilterValues, selectedPills, disableYaxisOppositeOnPillCount]);


        const renderWidget = (widget: any, index: number) => {

            const componentData = widget.formatter(
                widgetConfig,
                widget,
                widgetData
            )?.componentData;

            const finalIsLoading = componentData?.isLoading;
            const chartSyncId = getChartSyncId(widget);
            const chartComponentRef = getChartRef(chartSyncId);

            return (
                <div
                    key={`widget-container-${index}`}
                    className={`h-100 ${index > 0 ? "mt-3" : ""}`}
                >
                    <widget.widgetComponent
                        title={
                            widget.title
                        }
                        subTitle={
                            widget?.subTitle ||
                            ""
                        }
                        infoMessage={
                            widget.infoMessage ||
                            ""
                        }
                        widgetData={componentData}
                        {...componentData}
                        hasChartPillsFilters={hasChartPillsFilters}
                        widgetConfig={widgetConfig}
                        widget={widget}
                        isLoading={finalIsLoading}
                        identifier={widget.identifier}
                        tabKey={widgetConfig.tabKey}
                        yAxisFilter={parentFilterValues?.yAxisFilter}
                        yAxisOppositeFilter={parentFilterValues?.yAxisOppositeFilter}
                        selectedPills={selectedPills}
                        pillFilterDropDownData={pillFilterDropDownData[currentTabKey]}
                        disableYaxisOppositeOnPillCount={disableYaxisOppositeOnPillCount}
                        enableTooltipSync={enableTooltipSync}
                        chartSyncId={chartSyncId}
                        chartComponentRef={chartComponentRef}
                    />
                </div>
            );
        };

        const renderWidgetStructure = (
            structure: any,
            keyPrefix: string = ""
        ): any => {

            if (Array.isArray(structure)) {
                // Check if all elements are widgets (objects with type property)
                const allAreWidgets = structure.every(
                    (item) =>
                        item &&
                        typeof item === "object" &&
                        item.type &&
                        !Array.isArray(item)
                );

                if (allAreWidgets) {
                    // This is a row of widgets - render them in a Row with Cols
                    return (
                        <Row
                            className="bg-white w-100"
                            key={`row-${keyPrefix}`}
                            style={{ height: "100%" }}
                        >
                            {structure.map((widget, index) => {
                                if (widget && typeof widget === "object" && widget.type) {
                                    const componentData =
                                        widget.formatter(
                                            widgetConfig,
                                            widget,
                                            widgetData,
                                        )?.componentData;

                                    const finalIsLoading = componentData?.isLoading;
                                    const chartSyncId = getChartSyncId(widget);
                                    const chartComponentRef = getChartRef(chartSyncId);

                                    return (
                                        <Col span={24} key={`single-widget-${keyPrefix}-${index}`}>
                                            <widget.widgetComponent
                                                title={
                                                    widget.title
                                                }
                                                subTitle={
                                                    widget?.subTitle ||
                                                    ""
                                                }
                                                infoMessage={
                                                    widget.infoMessage ||
                                                    ""
                                                }
                                                widgetData={componentData}
                                                {...componentData}
                                                hasChartPillsFilters={hasChartPillsFilters}
                                                widgetConfig={widgetConfig}
                                                widget={widget}
                                                isLoading={finalIsLoading}
                                                identifier={widget.identifier}
                                                tabKey={widgetConfig.tabKey}
                                                yAxisFilter={parentFilterValues?.yAxisFilter}
                                                yAxisOppositeFilter={parentFilterValues?.yAxisOppositeFilter}
                                                selectedPills={selectedPills}
                                                pillFilterDropDownData={pillFilterDropDownData[currentTabKey]}
                                                disableYaxisOppositeOnPillCount={disableYaxisOppositeOnPillCount}
                                                enableTooltipSync={enableTooltipSync}
                                                chartSyncId={chartSyncId}
                                                chartComponentRef={chartComponentRef}
                                            />
                                        </Col>
                                    );
                                }
                                return null;
                            })}
                        </Row>
                    );
                } else {
                    // Check for the specific pattern: [nested_grid, single_widgets...]
                    // This handles cases like [[[w1,w2],[w3,w4]], [w5]]
                    const hasNestedGridPattern = structure.some(item =>
                        Array.isArray(item) && item.some(subItem => Array.isArray(subItem))
                    );

                    if (hasNestedGridPattern) {
                        // Render as a single row with columns for each top-level item
                        return (
                            <Row
                                className="bg-white w-100 px-2 pb-2"
                                key={`nested-row-${keyPrefix}`}
                                style={{ height: "100%" }}
                            >
                                {structure.map((item, index) => {
                                    if (Array.isArray(item)) {
                                        // Check if this item contains nested arrays (like [[w1,w2],[w3,w4]])
                                        const isNestedGrid = item.some(subItem => Array.isArray(subItem));

                                        if (isNestedGrid) {

                                            return (
                                                <Col
                                                    span={24}
                                                    key={`nested-grid-${keyPrefix}-${index}`}
                                                    className=""
                                                >
                                                    {item.map((subRow, subIndex) => (
                                                        <Row
                                                            gutter={[8, 0]}
                                                            className={
                                                                subIndex > 0 ? "mt-2" : ""
                                                            }
                                                            key={`sub-row-${keyPrefix}-${index}-${subIndex}`}
                                                        >
                                                            {subRow.map(
                                                                (
                                                                    widget: any,
                                                                    widgetIndex: number
                                                                ) => {
                                                                    const componentData =
                                                                        widget.formatter(
                                                                            widgetConfig,
                                                                            widget,
                                                                            widgetData,
                                                                        )?.componentData;

                                                                    const finalIsLoading = componentData?.isLoading;
                                                                    const chartSyncId = getChartSyncId(widget);
                                                                    const chartComponentRef = getChartRef(chartSyncId);

                                                                    const widgetComponent = (
                                                                        <widget.widgetComponent
                                                                            title={
                                                                                widget.title
                                                                            }
                                                                            subTitle={
                                                                                widget?.subTitle ||
                                                                                ""
                                                                            }
                                                                            infoMessage={
                                                                                widget.infoMessage ||
                                                                                ""
                                                                            }
                                                                            widgetData={componentData}
                                                                            {...componentData}
                                                                            hasChartPillsFilters={hasChartPillsFilters}
                                                                            widgetConfig={widgetConfig}
                                                                            widget={widget}
                                                                            isLoading={finalIsLoading}
                                                                            identifier={widget.identifier}
                                                                            tabKey={widgetConfig.tabKey}
                                                                            yAxisFilter={parentFilterValues?.yAxisFilter}
                                                                            yAxisOppositeFilter={parentFilterValues?.yAxisOppositeFilter}
                                                                            selectedPills={selectedPills}
                                                                            pillFilterDropDownData={pillFilterDropDownData[currentTabKey]}
                                                                            disableYaxisOppositeOnPillCount={disableYaxisOppositeOnPillCount}
                                                                            enableTooltipSync={enableTooltipSync}
                                                                            chartSyncId={chartSyncId}
                                                                            chartComponentRef={chartComponentRef}
                                                                        />
                                                                    );

                                                                    return (<Col
                                                                        xs={24}
                                                                        md={12}
                                                                        key={`${widget.type}-${widgetIndex}`}
                                                                    >
                                                                        {widget.type === WidgetType.W3 ? (
                                                                            <WidgetCard bodyPaddingClass="p-0">
                                                                                {widgetComponent}
                                                                            </WidgetCard>
                                                                        ) : (
                                                                            widgetComponent
                                                                        )}
                                                                    </Col>)
                                                                }
                                                            )}
                                                        </Row>
                                                    ))}
                                                </Col>
                                            );
                                        } else {
                                            // This is a simple array of widgets - render in a column with 1/3 width
                                            return (
                                                <Col
                                                    span={8}
                                                    className="h-100"
                                                    key={`simple-array-${keyPrefix}-${index}`}
                                                >
                                                    {item.map(
                                                        (
                                                            widget: any,
                                                            widgetIndex: number
                                                        ) => {
                                                            const componentData =
                                                                widget.formatter(
                                                                    widgetConfig,
                                                                    widget,
                                                                    widgetData
                                                                )?.componentData;

                                                            const finalIsLoading = componentData?.isLoading;
                                                            const chartSyncId = getChartSyncId(widget);
                                                            const chartComponentRef = getChartRef(chartSyncId);

                                                            return (
                                                                <div
                                                                    key={`widget-container-${widgetIndex}`}
                                                                    className={`h-100 ${widgetIndex > 0
                                                                        ? "mt-3"
                                                                        : ""
                                                                        }`}
                                                                >
                                                                    <widget.widgetComponent
                                                                        title={
                                                                            widget.title
                                                                        }
                                                                        subTitle={
                                                                            widget?.subTitle ||
                                                                            ""
                                                                        }
                                                                        infoMessage={
                                                                            widget.infoMessage ||
                                                                            ""
                                                                        }
                                                                        widgetData={componentData}
                                                                        {...componentData}
                                                                        hasChartPillsFilters={hasChartPillsFilters}
                                                                        widgetConfig={widgetConfig}
                                                                        widget={widget}
                                                                        isLoading={finalIsLoading}
                                                                        identifier={widget.identifier}
                                                                        tabKey={widgetConfig.tabKey}
                                                                        yAxisFilter={parentFilterValues?.yAxisFilter}
                                                                        yAxisOppositeFilter={parentFilterValues?.yAxisOppositeFilter}
                                                                        selectedPills={selectedPills}
                                                                        pillFilterDropDownData={pillFilterDropDownData[currentTabKey]}
                                                                        disableYaxisOppositeOnPillCount={disableYaxisOppositeOnPillCount}
                                                                        enableTooltipSync={enableTooltipSync}
                                                                        chartSyncId={chartSyncId}
                                                                        chartComponentRef={chartComponentRef}
                                                                    />
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </Col>
                                            );
                                        }
                                    } else if (item && typeof item === 'object' && item.type) {
                                        // Single widget
                                        const colSpan = 24 / structure.length;
                                        return (
                                            <Col span={colSpan} key={`single-widget-${keyPrefix}-${index}`}>
                                                {renderWidget(item, index)}
                                            </Col>
                                        );
                                    }
                                    return null;
                                })}
                            </Row>
                        );
                    } else {
                        // This is a regular nested structure - recurse further
                        return structure.map((item: any, index: number) =>
                            renderWidgetStructure(item, `${keyPrefix}-${index}`)
                        );
                    }
                }
            } else if (structure && typeof structure === 'object' && structure.type) {
                // This is a single widget
                return renderWidget(structure, 0);
            }

            return null;
        };

        const renderedWidgets = useMemo(() => {
            if (!widgetConfig?.widgets) return null;
            return renderWidgetStructure(widgetConfig.widgets);
        }, [
            widgetConfig?.widgets,
            widgetData,
            parentFilterValues,
            selectedPills?.length,
            pillsData?.length,
            pillFilterDropDownData[currentTabKey],
            shouldShowLoading,
            trendsFilters
        ]);

        const isCardRequired = widgetConfig?.config?.layout?.type === "card";
        const isBodyPaddingReq = widgetConfig?.config?.layout?.isBodyPaddingReq;
        const isTitleandIconRequired = widgetConfig?.config?.layout?.isTitleandIconRequired;

        if (isCardRequired) {
            return (
                <WidgetCard
                    overwriteDefaultClass={!isCardRequired}
                    className={
                        !isCardRequired
                            ? "bg-transparent border-0 rounded-0 shadow-none"
                            : "p-0"
                    }
                    bodyPaddingClass={isBodyPaddingReq ? "" : "p-0"}
                    bodyStyle={{ overflow: "hidden" }}
                    style={{ height: "100%" }}
                >
                    {isCardRequired && (
                        <div className="d-flex w-100 justify-content-between px-2 py-2 border-bottom">
                            {
                                isTitleandIconRequired && (

                                    <CardHeaderSection
                                        title={title || "Channel wise Performance"}
                                        titleIcon={widgetConfig?.icon}
                                    />
                                )}

                            {buildWidgetCardFilters({
                                filters: [
                                    ...parentFilters,
                                ],
                                positionFilters: FilterPosition.START
                            })}
                            {buildWidgetCardFilters({
                                filters: [
                                    {
                                        type: RevstackFilterTypes.SELECT,
                                        section: "",
                                        props: {
                                            options: REVSTACK_DRILL_DOWN_PERIOD_OPTIONS,
                                            value: selectedTimePeriod || DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS,
                                            isCustomDisplayMode: false,
                                            onChange: (value: DATE_PICKER_PRESET_PERIODS) => {
                                                handlePeriodAndGranularityChange?.(value, granularity!);
                                            },
                                            width: 150,
                                        },
                                        disabled: shouldShowLoading
                                    },
                                    {
                                        type: RevstackFilterTypes.RADIO,
                                        section: "",
                                        props: {
                                            options: granularityOptions,
                                            defaultValue: granularity || FrequencySlicerOptions.Daily,
                                            onChange: (value: RevstackFrequencySlicerOptions) => {
                                                handlePeriodAndGranularityChange?.(selectedTimePeriod!, value);
                                            },

                                        },
                                        disabled: shouldShowLoading
                                    },
                                ],
                                positionFilters: FilterPosition.END
                            })}
                        </div>
                    )}
                    {/* Pills Filters Section */}
                    {hasChartPillsFilters && (
                        <div className="chart-filters-container d-flex justify-content-between align-items-center py-3 px-3">
                            {
                                shouldShowLoading || (!selectedPills || selectedPills.length === 0) ?
                                    <Skeleton.Node
                                        className="w-100"
                                        active
                                        style={{ height: "50px" }}
                                    /> : <div className="d-flex gap-2 flex-wrap">
                                        {
                                            selectedPills?.map((item: any, index: number) => {
                                                let name: string, value: string, color: string;

                                                name = item.name;
                                                value = item[primaryDimension] || item.value || "";
                                                color = item.color || graphColors[index];

                                                return (
                                                    <span
                                                        key={`${name}-${value}`}
                                                        className="pill-container"
                                                        style={{
                                                            background: `${color}20`,
                                                            border: `1px solid ${color}`,
                                                        }}
                                                    >
                                                        <span className="d-flex align-items-center">
                                                            <span
                                                                className="pill-color-indicator"
                                                                style={{
                                                                    background: color,
                                                                }}
                                                            />
                                                            {name?.length > 10
                                                                ? `${capitalizePillNames ? capitalizeWords(name) : name}`.substring(0, 18) + '...'
                                                                : capitalizePillNames ? capitalizeWords(name) : name}
                                                        </span>
                                                        {isRemovablePillsRequired && (
                                                            <span
                                                                className="pill-remove-btn"
                                                                onClick={() => handleRemovePill(value)}
                                                            >
                                                                X
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                    </div>
                            }
                            {hasFilterDropDown && !shouldShowLoading && selectedPills.length !== 0 && (
                                <div className="filters-select d-flex justify-content-end">
                                    <RevstackTrendsSelectDropDown
                                        addPillsBtnTitle={addPillsBtnTitle}
                                        dropdownItems={chartPillFiltersOptions}
                                        selectedPills={selectedPills}
                                        setSelectedPills={setSelectedPills}
                                        onAddPills={handleAddPill}
                                        onRemovePills={handleRemovePill}
                                        disableDropDown={shouldShowLoading}
                                        maxAllowedPills={maxPillsFiltersAllowed}
                                        dropdownFilterPlaceHolder={dropdownFilterPlaceHolder}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {renderedWidgets}
                </WidgetCard>
            );
        }

        return <div>{renderedWidgets}</div>;
    };

export default RevstackGenericChartParentContainer
